import cx from 'classnames'
import {
  type ForwardedRef,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Texture,
  TextureLoader,
  sRGBEncoding,
  DirectionalLight,
  MeshStandardMaterial,
  MeshBasicMaterial,
  Geometry,
  Mesh,
  Math as ThreeMath,
  Color,
} from 'three'
import { debounce } from 'throttle-debounce'

import { generateFrame, updateFrame } from '../../lib/frame-generator'
import { type FrameSettings, getFrameSettings } from '../../lib/frame-viewer'
import { generateTextureUrlFromImage } from '../../lib/frame-viewer-image'
import { useSpring } from '../../lib/spring'
import { UVMapViewer } from '../../lib/uv-map-viewer'
import { FrameBuilderDefaultFrameContext } from '../../contexts/default-frame-context'
import { FrameBuilderConfigurationContext } from '../../contexts/configuration-context'
import { FrameBuilderSelectedStateContext } from '../../contexts/state-context'
import {
  type CameraSettings,
  fitCameraToObject,
  initializeCamera,
} from './camera'
import { initializeLight } from './light'
import { initializeRenderer } from './renderer'
import { initializeScene } from './scene'

// Set this to true, to enable UV debugger for artwork.
const DEBUG_UV = false

const CAMERA_FIT_RATIO = 1.3
const CAMERA_FIT_PER_RAD = 0.01
const CAMERA_ROTATION = -45
const CAMERA_SETTINGS: CameraSettings = {
  FOV: 30,
}

interface FrameViewerProps {
  width?: number
  height?: number
  className?: string
}

function useForwardRef<T>(ref: ForwardedRef<T>, initialValue: T) {
  const targetRef = useRef<T>(initialValue)

  useEffect(() => {
    if (!ref) {
      return
    }

    if (typeof ref === 'function') {
      ref(targetRef.current)
      return
    }

    ref.current = targetRef.current
  }, [ref])

  return targetRef
}

const FrameViewer = forwardRef<HTMLCanvasElement, FrameViewerProps>(
  ({ width, height, className }, ref) => {
    const { selectedState } = useContext(FrameBuilderSelectedStateContext)
    const configuration = useContext(FrameBuilderConfigurationContext)
    const { defaultState } = useContext(FrameBuilderDefaultFrameContext)

    const canvasRef = useForwardRef<HTMLCanvasElement | null>(ref, null)

    const textureLoaderRef = useRef(new TextureLoader())
    const sceneRef = useRef<Scene>()
    const cameraRef = useRef<PerspectiveCamera>()
    const rendererRef = useRef<WebGLRenderer>()
    const artworkUVViewerRef = useRef<UVMapViewer>()
    const lightRef = useRef<DirectionalLight>()
    const frameGeometryRef = useRef<Geometry>()
    const frameMaterialRef = useRef<MeshStandardMaterial>()
    const frameMeshRef = useRef<Mesh>()
    const passepartoutGeometryRef = useRef<Geometry>()
    const passepartoutMaterialRef = useRef<MeshBasicMaterial>()
    const passepartoutMeshRef = useRef<Mesh>()
    const artworkGeometryRef = useRef<Geometry>()
    const artworkMaterialRef = useRef<MeshStandardMaterial>()
    const artworkMeshRef = useRef<Mesh>()

    const frameSettings = useMemo<FrameSettings>(
      () => getFrameSettings(selectedState, defaultState, configuration),
      [selectedState, defaultState, configuration],
    )

    const [threeIsReady, setThreeIsReady] = useState(false)
    const [artworkUrl, setArtworkUrl] = useState<string | undefined>(
      frameSettings.artworkUrl,
    )
    const [artworkTexture, setArtworkTexture] = useState<Texture | null>(null)
    const [frameDiffuseUrl, setFrameDiffuseUrl] = useState<string | undefined>(
      frameSettings.frameTexture?.diffuseUrl,
    )
    const [frameDiffuseTexture, setFrameDiffuseTexture] =
      useState<Texture | null>(null)
    const [rotationTarget, setRotationTarget] = useState(0)

    // Camera rotation animation
    const rotation = useSpring(rotationTarget, 1, 10)
    const fitCameraToFrame = () => {
      if (cameraRef.current && frameMeshRef.current) {
        fitCameraToObject(
          cameraRef.current,
          frameMeshRef.current,
          CAMERA_FIT_RATIO,
          ThreeMath.degToRad(rotation),
          CAMERA_FIT_PER_RAD,
        )
      }
    }

    const debounceUpdateArtworkTexture = useRef(
      debounce<(frameSettings: FrameSettings) => Promise<void>>(
        400,
        async (frameSettings: FrameSettings) => {
          if (!frameSettings.artworkUrl) {
            return
          }

          const textureUrl = await generateTextureUrlFromImage(
            frameSettings.artworkUrl,
            frameSettings.artWidth,
            frameSettings.artHeight,
          )

          textureLoaderRef.current.load(textureUrl, (texture) => {
            texture.encoding = sRGBEncoding

            setArtworkUrl(textureUrl)
            setArtworkTexture(texture)

            if (DEBUG_UV && artworkUVViewerRef.current) {
              artworkUVViewerRef.current.setTexture(texture)
            }
          })
        },
      ),
    )

    const updateFrameTexture = (forceUpdate = false) => {
      // Unset frame texture
      if (!frameSettings.frameTexture?.diffuseUrl) {
        setFrameDiffuseUrl('')
        setFrameDiffuseTexture(null)
        return
      }

      // Set frame texture
      if (
        forceUpdate ||
        !frameDiffuseTexture ||
        frameDiffuseUrl !== frameSettings.frameTexture.diffuseUrl
      ) {
        textureLoaderRef.current.load(
          frameSettings.frameTexture.diffuseUrl,
          (texture) => {
            texture.encoding = sRGBEncoding

            setFrameDiffuseUrl(frameSettings.frameTexture?.diffuseUrl)
            setFrameDiffuseTexture(texture)
          },
        )
      }
    }

    // Initialize scene primitives (re-initialize on resize)
    useEffect(() => {
      if (!canvasRef.current || !width || !height) {
        setThreeIsReady(false)
        return
      }

      sceneRef.current = initializeScene()
      cameraRef.current = initializeCamera(
        canvasRef.current.width / canvasRef.current.height,
        CAMERA_SETTINGS,
      )
      rendererRef.current = initializeRenderer(
        canvasRef.current,
        window.devicePixelRatio,
      )

      setThreeIsReady(true)

      // Initialize scene objects
      const scene = sceneRef.current

      lightRef.current = initializeLight(sceneRef.current)

      // Create geometries
      frameGeometryRef.current = new Geometry()
      frameGeometryRef.current.name = 'FrameGeometry'
      passepartoutGeometryRef.current = new Geometry()
      passepartoutGeometryRef.current.name = 'PassepartoutGeometry'
      artworkGeometryRef.current = new Geometry()
      artworkGeometryRef.current.name = 'ArtworkGeometry'

      generateFrame(
        frameGeometryRef.current,
        passepartoutGeometryRef.current,
        artworkGeometryRef.current,
        frameSettings,
      )

      // Create materials
      frameMaterialRef.current = new MeshStandardMaterial({
        color: 'black',
        roughness: 1,
        metalness: 0,
      })
      frameMaterialRef.current.name = 'FrameMaterial'

      if (frameSettings.passepartoutWidth > 0) {
        passepartoutMaterialRef.current = new MeshBasicMaterial({
          color: frameSettings.passepartoutColor,
          reflectivity: 0,
        })
        passepartoutMaterialRef.current.name = 'PassepartoutMaterial'
      }

      artworkMaterialRef.current = new MeshStandardMaterial({
        color: 'white',
        roughness: 1,
        metalness: 0,
      })
      artworkMaterialRef.current.name = 'ArtworkMaterial'

      // Create meshes
      frameMeshRef.current = new Mesh(
        frameGeometryRef.current,
        frameMaterialRef.current,
      )
      frameMeshRef.current.castShadow = true
      frameMeshRef.current.receiveShadow = true
      frameMeshRef.current.name = 'FrameMesh'

      if (frameSettings.passepartoutWidth > 0) {
        passepartoutMeshRef.current = new Mesh(
          passepartoutGeometryRef.current,
          passepartoutMaterialRef.current,
        )
        passepartoutMeshRef.current.castShadow = false
        passepartoutMeshRef.current.receiveShadow = false
        passepartoutMeshRef.current.name = 'PassepartoutMesh'
      }

      artworkMeshRef.current = new Mesh(
        artworkGeometryRef.current,
        artworkMaterialRef.current,
      )
      artworkMeshRef.current.castShadow = false
      artworkMeshRef.current.receiveShadow = false
      artworkMeshRef.current.name = 'ArtworkMesh'

      // Update scene
      scene.add(frameMeshRef.current, artworkMeshRef.current)

      if (frameSettings.passepartoutWidth > 0 && passepartoutMeshRef.current) {
        scene.add(passepartoutMeshRef.current)
      }

      // Create UV viewver
      artworkUVViewerRef.current = new UVMapViewer(
        artworkMeshRef.current,
        undefined,
        {
          strokeStyle: `red`,
          strokeWidth: 3,
        },
        canvasRef.current,
      )

      fitCameraToFrame()

      // Update artwork image texture
      if (frameSettings.artworkUrl) {
        debounceUpdateArtworkTexture.current(frameSettings)
      }

      updateFrameTexture(true)
    }, [threeIsReady, width, height, canvasRef])

    // Update artwork image
    useEffect(() => {
      if (!frameSettings.artworkUrl) {
        setArtworkUrl('')
        setArtworkTexture(null)
        return
      }

      if (artworkUrl != frameSettings.artworkUrl || !artworkTexture) {
        debounceUpdateArtworkTexture.current(frameSettings)
      }
    }, [
      frameSettings.artworkUrl,
      frameSettings.artWidth,
      frameSettings.artHeight,
    ])

    // Map artwork texture to artwork mesh
    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      if (!artworkMaterialRef.current) {
        return
      }

      if (artworkTexture) {
        artworkMaterialRef.current.map = artworkTexture
        // Using null is necessary for Three.js to use texture instead of a solid color
        // @ts-expect-error
        artworkMaterialRef.current.color = null
        artworkMaterialRef.current.needsUpdate = true

        return
      }

      artworkMaterialRef.current.map = null
      artworkMaterialRef.current.color = new Color('white')
      artworkMaterialRef.current.needsUpdate = true
    }, [threeIsReady, artworkTexture])

    // Update frame color/texture image
    useEffect(() => {
      updateFrameTexture()
    }, [frameSettings.frameTexture])

    // Map frame texture to frame mesh
    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      if (!frameMaterialRef.current) {
        return
      }

      frameMaterialRef.current.map = frameDiffuseTexture ?? null
      // Using null is necessary for Three.js to use texture instead of a solid color
      // @ts-expect-error
      frameMaterialRef.current.color = frameDiffuseTexture
        ? null
        : new Color('black')
      frameMaterialRef.current.needsUpdate = true
    }, [threeIsReady, frameDiffuseTexture])

    // Update passepartout size
    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      if (!sceneRef.current) {
        return
      }

      if (frameSettings.passepartoutWidth > 0) {
        if (!passepartoutMaterialRef.current) {
          passepartoutMaterialRef.current = new MeshBasicMaterial({
            color: frameSettings.passepartoutColor,
            reflectivity: 0,
          })
          passepartoutMaterialRef.current.name = 'PassepartoutMaterial'
        }

        if (!passepartoutMeshRef.current) {
          passepartoutMeshRef.current = new Mesh(
            passepartoutGeometryRef.current,
            passepartoutMaterialRef.current,
          )
          passepartoutMeshRef.current.receiveShadow = true
          passepartoutMeshRef.current.name = 'PassepartoutMesh'

          sceneRef.current.add(passepartoutMeshRef.current)
        }

        return
      }

      if (passepartoutMeshRef.current) {
        sceneRef.current.remove(passepartoutMeshRef.current)
        passepartoutMeshRef.current = undefined
      }

      if (passepartoutMaterialRef.current) {
        passepartoutMaterialRef.current.dispose()
        passepartoutMaterialRef.current = undefined
      }
    }, [threeIsReady, frameSettings.passepartoutWidth])

    // Update passepartout color
    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      if (!passepartoutMaterialRef.current) {
        return
      }

      passepartoutMaterialRef.current.setValues({
        color: frameSettings.passepartoutColor,
      })
    }, [threeIsReady, frameSettings.passepartoutColor])

    // Update frame dimensions
    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      if (
        !frameGeometryRef.current ||
        !passepartoutGeometryRef.current ||
        !artworkGeometryRef.current
      ) {
        return
      }

      updateFrame(
        frameGeometryRef.current,
        passepartoutGeometryRef.current,
        artworkGeometryRef.current,
        frameSettings,
      )
      fitCameraToFrame()
    }, [
      threeIsReady,
      frameSettings.artHeight,
      frameSettings.artWidth,
      frameSettings.borderWidth,
      frameSettings.passepartoutWidth,
      frameSettings.inset,
    ])

    // Update width/height related stuff when necessary
    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      if (!rendererRef.current || !cameraRef.current || !width || !height) {
        return
      }

      cameraRef.current.aspect = width / height
      fitCameraToFrame()

      rendererRef.current.setSize(width, height)

      // Debugging view
      if (DEBUG_UV && artworkUVViewerRef.current) {
        artworkUVViewerRef.current.setSize(width * 0.3, width * 0.3)
      }
    }, [threeIsReady, rendererRef.current, cameraRef.current, width, height])

    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      fitCameraToFrame()
    }, [rotation])

    // Rerender when necessary
    useEffect(() => {
      if (!threeIsReady) {
        return
      }

      if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
        return
      }

      rendererRef.current.render(sceneRef.current, cameraRef.current)

      if (DEBUG_UV && artworkUVViewerRef.current) {
        artworkUVViewerRef.current.render(rendererRef.current)
      }
    }, [
      threeIsReady,
      frameSettings.artWidth,
      frameSettings.artHeight,
      frameSettings.borderWidth,
      frameSettings.passepartoutWidth,
      frameSettings.passepartoutColor,
      artworkTexture,
      frameDiffuseTexture,
      rotation,
    ])

    return (
      <canvas
        width={width}
        height={height}
        ref={canvasRef}
        onMouseEnter={() => setRotationTarget(CAMERA_ROTATION)}
        onMouseLeave={() => setRotationTarget(0)}
        onTouchStart={() => setRotationTarget(CAMERA_ROTATION)}
        onTouchEnd={() => setRotationTarget(0)}
        className={cx('block select-none', className)}
      />
    )
  },
)

FrameViewer.displayName = 'FrameViewer'

export default FrameViewer
